import { useRef, useState, type ComponentProps } from "react";
import { Input } from "@/ui/input";

type InputProps = Omit<
  ComponentProps<typeof Input>,
  "value" | "defaultValue" | "onChange" | "type" | "inputMode"
>;

type Props = InputProps & {
  value: number | null;
  onChange: (v: number | null) => void;
  /** id của dòng chú thích dưới ô, nếu bên gọi có vẽ một dòng. */
  describedBy?: string;
  /**
   * Cho gõ dấu trừ ở đầu ô — chỉ dành cho khoản điều chỉnh có thể âm. Mặc
   * định ô chỉ nhận số từ 0 trở lên, dấu trừ bị bỏ như mọi ký tự khác.
   */
  allowNegative?: boolean;
};

const formatter = new Intl.NumberFormat("vi-VN");

/**
 * Ô tiền kiểu Việt: tự chấm hàng nghìn khi gõ ("1500000" hiện "1.500.000"),
 * gõ hay dán sẵn dấu chấm cũng đọc đúng — chỉ giữ chữ số (CEO chốt
 * 24/09/2026 cho mọi ô tiền). Các thuộc tính khác (aria-label, autoFocus,
 * onKeyDown, className…) đi thẳng xuống ô.
 */
export function MoneyInput({
  value,
  onChange,
  placeholder,
  describedBy,
  allowNegative = false,
  "aria-describedby": ariaDescribedBy,
  onFocus,
  onMouseUp,
  onBlur,
  ...rest
}: Props) {
  // Mới gõ mỗi dấu trừ thì chưa có số nào để đưa lên `value`; nhớ riêng để ô
  // không nuốt dấu trừ trước khi chữ số đầu tiên tới.
  const [pendingMinus, setPendingMinus] = useState(false);
  // Ô mở sẵn "0" (đặt cọc, giảm giá tay, dòng mới…): vào ô là bôi đen số 0 để
  // gõ đè (CEO chốt 24/09/2026). Trước đó bấm vào mép trái thì con trỏ đứng
  // TRƯỚC số 0 và gõ 500.000 thành 5.000.000.
  const zeroSelected = useRef(false);
  return (
    <Input
      {...rest}
      // Bàn phím số của điện thoại không có dấu trừ.
      inputMode={allowNegative ? "text" : "numeric"}
      aria-describedby={describedBy ?? ariaDescribedBy}
      value={value === null ? (pendingMinus ? "-" : "") : formatter.format(value)}
      placeholder={placeholder ?? "0 ₫"}
      onFocus={(e) => {
        if (value === 0) {
          e.currentTarget.setSelectionRange(0, e.currentTarget.value.length);
          zeroSelected.current = true;
        }
        onFocus?.(e);
      }}
      onMouseUp={(e) => {
        // Chrome/Safari đặt lại con trỏ ở nhịp nhả chuột của chính cú bấm vừa
        // đưa vào ô — chặn đúng nhịp đó kẻo mất vùng bôi đen.
        if (zeroSelected.current) {
          zeroSelected.current = false;
          e.preventDefault();
        }
        onMouseUp?.(e);
      }}
      onBlur={(e) => {
        zeroSelected.current = false;
        onBlur?.(e);
      }}
      onChange={(e) => {
        const raw = e.target.value;
        const negative = allowNegative && /^\s*[-−]/.test(raw);
        // Dấu phẩy đánh dấu phần lẻ — tiền không có số lẻ, nên BỎ hẳn phần sau
        // dấu phẩy ĐẦU TIÊN thay vì ghép nó vào số nguyên ("1,5" từng thành 15, "1.000,50" từng thành 100050).
        const beforeComma = raw.split(",", 1)[0] ?? "";
        // Dán/điền cả chuỗi một lần (paste, kéo-thả, hoặc set value bằng tay)
        // báo `inputType` là "insertFromPaste"/rỗng — khác hẳn gõ từng phím
        // (`insertText`/xoá), lúc đó formatter đã tự chấm hàng nghìn ở lượt
        // render TRƯỚC nên cụm sau dấu chấm luôn dở dang (vd "500." mới gõ,
        // chưa đủ 3 số) — validate nhóm ở nhánh gõ phím là tự cắt số đang gõ
        // dở của chính người dùng.
        const inputType = (e.nativeEvent as InputEvent).inputType;
        const pasteLike =
          inputType === undefined ||
          inputType === "insertFromPaste" ||
          inputType === "insertFromDrop" ||
          inputType === "insertFromPasteAsQuotation";
        let kept: string;
        if (pasteLike) {
          // Dấu chấm / khoảng trắng là phân cách hàng nghìn — CHỈ hợp lệ khi
          // cụm đứng SAU nó có ĐÚNG 3 chữ số ("1.500.000", "1 500 000"). Một
          // dấu chấm không theo nhóm 3 (vd "1.5", "1.00") nhiều khả năng dán
          // nhầm dấu thập phân — cắt bỏ từ đó, không ghép phần sau vào số
          // nguyên (vd "1.5" từng thành 15, "1.00" từng
          // thành 100).
          const groups = beforeComma.split(/[.\s]+/);
          kept = groups[0] ?? "";
          for (let i = 1; i < groups.length; i += 1) {
            const group = groups[i] ?? "";
            if (!/^\d{3}$/.test(group)) break;
            kept += group;
          }
        } else {
          // Gõ từng phím: dấu chấm/khoảng trắng luôn là hàng nghìn do formatter
          // tự thêm — giữ nguyên nếp cũ (bỏ mọi ký tự không phải số) để không
          // cắt nhầm số đang gõ dở.
          kept = beforeComma;
        }
        const digits = kept.replace(/\D/g, "");
        setPendingMinus(negative && !digits);
        if (!digits) {
          onChange(null);
          return;
        }
        // Số dài quá khoảng an toàn KHÔNG ghim trần ở đây: đưa nguyên cho form
        // để `.int(tooBigInt(...))` báo "<tên ô> quá lớn — kiểm tra lại số".
        // Ghim lặng lẽ là nuốt mất lời báo đó và lưu một con số người dùng
        // không hề gõ.
        const n = Number(digits);
        onChange(negative && n !== 0 ? -n : n);
      }}
    />
  );
}
